import { useCallback, useEffect, useState } from 'react'
import { appsyncQuery } from '../../../api/appsync-client.js'
import { TASKS_ASSIGNED_TO_ME } from '../graphql.js'
import { TASK_FOR_ME_CHANGED } from '../../../components/tasks/graphql.js'
import { useSubscription } from '../../../api/useSubscription.js'

/**
 * Powers the live nav-bar badge for the Tasks entry.
 *
 * Counts tasks with status TODO | IN_PROGRESS assigned to the viewer.
 * Seeded from `tasksAssignedToMe` on mount, kept live via a single
 * user-scoped subscription (`taskForMeChanged`) that delivers events to
 * the current assignee AND — on reassignment — the previous assignee.
 *
 * Returns: { openCount, loading, refetch }
 *
 * History: earlier revisions fanned out one pair-scoped subscription per
 * (reportId, managerId) pair the user belonged to. That was brittle —
 * coverage depended on `usePairs` enumerating every relevant pair before
 * events arrived, and the initial-count query used a different scope
 * from the live feed, so the two could drift. The user-scoped
 * subscription eliminates that class of bug. See
 * docs/contributor/tasks-data-model.md.
 */
export function useTaskBadgeCount({ me, enabled = true } = {}) {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(enabled)
  const userId = me?.userId
  const active = enabled && !!userId

  const refetch = useCallback(async () => {
    if (!active) {
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const data = await appsyncQuery(TASKS_ASSIGNED_TO_ME, {
        statusFilter: 'TODO,IN_PROGRESS',
      })
      setTasks(data?.tasksAssignedToMe ?? [])
    } catch {
      // Badge is non-critical; swallow.
    } finally {
      setLoading(false)
    }
  }, [active])

  useEffect(() => {
    refetch()
  }, [refetch])

  const isMineAndOpen = useCallback(
    (task) =>
      task &&
      task.assignee === userId &&
      (task.status === 'TODO' || task.status === 'IN_PROGRESS'),
    [userId],
  )

  const handleEvent = useCallback(
    (task) => {
      if (!task) return
      setTasks((prev) => {
        const exists = prev.some((t) => t.taskId === task.taskId)
        const mine = isMineAndOpen(task)
        // Reassignment away from me: payload carries previousAssignee === me
        // but task.assignee is now someone else. Drop from local list.
        if (!mine) return exists ? prev.filter((t) => t.taskId !== task.taskId) : prev
        if (exists) return prev.map((t) => (t.taskId === task.taskId ? task : t))
        return [...prev, task]
      })
    },
    [isMineAndOpen],
  )

  useSubscription(
    TASK_FOR_ME_CHANGED,
    {},
    (data) => handleEvent(data?.taskForMeChanged),
    { enabled: active },
  )

  return {
    openCount: tasks.length,
    loading,
    refetch,
  }
}

/**
 * Convenience wrapper so call-sites can drop the hook into the nav shell
 * without owning the state themselves:
 *
 *     <TaskBadgeCountProvider me={me}>
 *       {({ openCount }) => <NavBadge count={openCount} />}
 *     </TaskBadgeCountProvider>
 */
export function TaskBadgeCountProvider({ me, enabled = true, children }) {
  const { openCount, loading, refetch } = useTaskBadgeCount({ me, enabled })
  return children({ openCount, loading, refetch })
}
